import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { messagingService } from '../../../services/messagingService';

const NewTeamGroupChatModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [groupName, setGroupName] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      setStep(1);
      setGroupName('');
      setSelectedParticipants([]);
      setError('');
      setSearchQuery('');
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const users = await messagingService?.getAllUsers();
      setAllUsers(users);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const toggleParticipant = (userId) => {
    setSelectedParticipants(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateGroupChat = async () => {
    if (!groupName?.trim()) {
      setError('Please enter a group name');
      return;
    }

    if (selectedParticipants?.length === 0) {
      setError('Please select at least one participant');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Check if a group chat with the same participants already exists
      const sortedParticipantIds = [...selectedParticipants, user?.id]?.sort();
      
      // Get all group conversations the current user is part of
      const { data: userConversations } = await supabase
        ?.from('conversation_participants')
        ?.select(`
          conversation_id,
          conversation:conversations(
            id,
            conversation_type,
            title
          )
        `)
        ?.eq('user_id', user?.id);

      // Check each conversation to see if it has the exact same participants
      for (const conv of userConversations || []) {
        if (conv?.conversation?.conversation_type === 'group') {
          // Get all participants for this conversation
          const { data: convParticipants } = await supabase
            ?.from('conversation_participants')
            ?.select('user_id')
            ?.eq('conversation_id', conv?.conversation_id);

          const convParticipantIds = convParticipants?.map(p => p?.user_id)?.sort();

          // Check if participants match exactly
          if (
            convParticipantIds?.length === sortedParticipantIds?.length &&
            convParticipantIds?.every((id, index) => id === sortedParticipantIds?.[index])
          ) {
            // Found an existing conversation with the same participants
            console.log('Found existing group chat with same participants:', conv?.conversation_id);
            onSuccess?.(conv?.conversation_id);
            onClose();
            return;
          }
        }
      }

      // No existing conversation found, create a new one
      const { data: result, error: rpcError } = await supabase?.rpc(
        'create_conversation_with_participants',
        {
          p_conversation_type: 'group',
          p_title: groupName?.trim(),
          p_team_id: null,
          p_participant_ids: selectedParticipants
        }
      );

      if (rpcError) throw rpcError;

      onSuccess?.(result);
      onClose();
    } catch (err) {
      console.error('Error creating group chat:', err);
      setError(err?.message || 'Failed to create group chat');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = allUsers?.filter(u => 
    u?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    u?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-popover border border-border rounded-lg shadow-elevated w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-popover-foreground">
              Create Custom Group Chat
            </h3>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
              <div className="flex items-start space-x-2">
                <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {step === 1 ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Group Name
                </label>
                <Input
                  type="text"
                  placeholder="Enter group name..."
                  value={groupName}
                  onChange={(e) => setGroupName(e?.target?.value)}
                  autoFocus
                />
              </div>

              <div className="flex justify-end">
                <Button
                  variant="default"
                  onClick={() => setStep(2)}
                  disabled={!groupName?.trim()}
                >
                  Next
                  <Icon name="ChevronRight" size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search participants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10"
                />
              </div>

              {/* Selected count */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">
                  {selectedParticipants?.length} participant{selectedParticipants?.length !== 1 ? 's' : ''} selected
                </span>
              </div>

              {/* User list */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : filteredUsers?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No users found</p>
                  </div>
                ) : (
                  filteredUsers?.map((u) => (
                    <div
                      key={u?.id}
                      className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted transition-micro cursor-pointer"
                      onClick={() => toggleParticipant(u?.id)}
                    >
                      <Checkbox
                        checked={selectedParticipants?.includes(u?.id)}
                        onChange={() => toggleParticipant(u?.id)}
                      />
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-primary-foreground">
                          {u?.fullName?.split(' ')?.map(n => n?.[0])?.join('') || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{u?.fullName}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {u?.displayRole}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="p-6 border-t border-border">
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
              >
                <Icon name="ChevronLeft" size={16} className="mr-2" />
                Back
              </Button>
              <Button
                variant="default"
                onClick={handleCreateGroupChat}
                disabled={loading || selectedParticipants?.length === 0}
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Icon name="Check" size={16} className="mr-2" />
                    Create Group
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewTeamGroupChatModal;